import { Select, type SelectOptionType } from '@bp/ui-components';
import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { SingleValue } from 'react-select';
import { organizationRolesAsString } from '../../../utils/organizationRolesAsString';
import type { ProfileQueryType } from './ImportPersonsForm';

export const AssignExistingProfile = ({
  matching,
  handleSelect,
}: {
  matching?: Partial<ProfileQueryType>[];
  handleSelect: (uuid: string) => void;
}) => {
  const { t } = useTranslation();

  // biome-ignore lint/correctness/useExhaustiveDependencies(t): ignore t
  const connectOpts = useMemo(
    () =>
      matching?.map((profile) => {
        const translatedRole = profile.organizationRoles
          ? `${organizationRolesAsString(profile.organizationRoles)}`
          : '';
        const selectName = profile.organizationRoles
          ? `${profile.selectName} - ${translatedRole}`
          : (profile.selectName ?? t('common.noName'));
        return {
          value: profile.uuid,
          label: selectName,
        };
      }) ?? [],
    [matching],
  );

  const initialValue = connectOpts.length === 1 ? connectOpts[0] : undefined;
  const singleMatchUuid = connectOpts.length === 1 ? connectOpts[0].value : undefined;
  const lastAutoSelectedUuidRef = useRef<string | undefined>(undefined);
  const handleSelectRef = useRef(handleSelect);

  // Keep handleSelectRef up to date
  useEffect(() => {
    handleSelectRef.current = handleSelect;
  }, [handleSelect]);

  useEffect(() => {
    if (singleMatchUuid && lastAutoSelectedUuidRef.current !== singleMatchUuid) {
      lastAutoSelectedUuidRef.current = singleMatchUuid;
      handleSelectRef.current(singleMatchUuid as string);
    }
  }, [singleMatchUuid]);

  return (
    <Select
      options={connectOpts}
      isSearchable={true}
      isClearable={true}
      onChange={(event) => {
        const assign = (event as SingleValue<SelectOptionType>)?.value as string;
        handleSelect(assign);
      }}
      value={initialValue}
      name={'assign'}
      placeholder={t('procuratImport.assignProfile')}
      dense
    />
  );
};
